import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { setActiveChild } from "./kingdom";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Authoritative landing dashboard for a signed-in user. Child session always wins
// (a child device must reopen in their own Kingdom, never the parent dashboard).
export function dashboardFor({ user, child }) {
  if (child) return `/kingdom/play/${child.id}`;
  if (user && user.role === "admin") return "/admin";
  if (user && user.role === "educator") return "/teacher";
  if (user) return "/kingdom";
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = signed out
  const [child, setChild] = useState(undefined); // child session (kid portal)

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  const childRefresh = useCallback(async () => {
    try {
      const { data } = await api.get("/child-auth/me");
      setChild(data);
      setActiveChild({ id: data.id, nickname: data.nickname, avatarId: data.avatarId, avatarLook: data.avatarLook });
      return data;
    } catch {
      setChild(null);
      return null;
    }
  }, []);

  useEffect(() => { refreshUser(); childRefresh(); }, [refreshUser, childRefresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Never allow a parent + child session simultaneously (shared-device safety).
    try { await api.post("/child-auth/logout"); } catch {}
    localStorage.removeItem("gk_active_child");
    setChild(null);
    setUser(data);
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    try { await api.post("/child-auth/logout"); } catch {}
    setChild(null);
    setUser(data);
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("gk_active_child");
    setUser(null);
  };

  // ---- Child (kid) portal session ----
  const childLogin = async (username, secret) => {
    const { data } = await api.post("/child-auth/login", { username, secret });
    // A child device must never carry a parent session.
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    setChild(data);
    setActiveChild({ id: data.id, nickname: data.nickname, avatarId: data.avatarId, avatarLook: data.avatarLook });
    return data;
  };
  const childLogout = async () => {
    try { await api.post("/child-auth/logout"); } catch {}
    localStorage.removeItem("gk_active_child");
    setChild(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refreshUser,
      child, childLogin, childLogout, childRefresh }}>
      {children}
    </AuthContext.Provider>
  );
}
